import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  Database, RefreshCw, Check, AlertTriangle,
  User, Building, Shield, Download, Upload, Smartphone, Lock
} from 'lucide-react';
import './Settings.css';

function Settings() {
  const { state, seedDatabase, setMasterPassword, verifyMasterPassword, hasMasterPassword, backfillSalesAnalyticsFields, backfillSalesMetrics } = useApp();
  const { user } = useAuth();
  const [seeding, setSeeding] = useState(false);
  const [message, setMessage] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState(null);
  const [metricsBackfilling, setMetricsBackfilling] = useState(false);
  const [metricsBackfillMessage, setMetricsBackfillMessage] = useState(null);

  // Master password state
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    hasMasterPassword().then(setHasExistingPassword);
  }, []);

  const handleSetMasterPassword = async () => {
    setPasswordMessage(null);

    if (hasExistingPassword) {
      if (!currentPassword.trim()) {
        setPasswordMessage({ type: 'error', text: 'Please enter your current master password.' });
        return;
      }
      const result = await verifyMasterPassword(currentPassword);
      if (!result.valid) {
        setPasswordMessage({ type: 'error', text: 'Current master password is incorrect.' });
        return;
      }
    }

    if (!newPassword.trim()) {
      setPasswordMessage({ type: 'error', text: 'Please enter a new master password.' });
      return;
    }
    if (newPassword.length < 4) {
      setPasswordMessage({ type: 'error', text: 'Master password must be at least 4 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }

    setSavingPassword(true);
    await setMasterPassword(newPassword);
    setHasExistingPassword(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordMessage({ type: 'success', text: 'Master password has been saved successfully.' });
    setSavingPassword(false);
  };

  const handleSeedDatabase = async () => {
    setSeeding(true);
    setMessage(null);

    const result = await seedDatabase();

    if (result.success) {
      setMessage({ type: 'success', text: 'Sample data loaded successfully!' });
    } else {
      setMessage({ type: 'error', text: `Error: ${result.error}` });
    }

    setSeeding(false);
  };

  const handleBackfillSalesAnalytics = async () => {
    if (!window.confirm('Backfill sales-analytics fields on every estimate record? Safe to re-run; only writes when fields are missing.')) {
      return;
    }
    setBackfilling(true);
    setBackfillMessage(null);
    const result = await backfillSalesAnalyticsFields();
    if (result.success) {
      setBackfillMessage({
        type: 'success',
        text: `Backfill complete. Updated ${result.updated} of ${result.total} estimate records (${result.skipped} already up to date).`,
      });
    } else {
      setBackfillMessage({ type: 'error', text: `Error: ${result.error}` });
    }
    setBackfilling(false);
  };

  const handleBackfillSalesMetrics = async () => {
    if (!window.confirm('Rebuild the trailing 12 months of salesMetrics daily docs from existing estimates? Safe to re-run; overwrites each daily doc.')) {
      return;
    }
    setMetricsBackfilling(true);
    setMetricsBackfillMessage(null);
    const result = await backfillSalesMetrics();
    if (result.success) {
      setMetricsBackfillMessage({
        type: 'success',
        text: `Sales metrics backfill complete. Wrote ${result.written} daily docs (${result.startDayKey} → ${result.endDayKey}) from ${result.leadsScanned} leads.`,
      });
    } else {
      setMetricsBackfillMessage({ type: 'error', text: `Error: ${result.error}` });
    }
    setMetricsBackfilling(false);
  };

  const dataCounts = {
    clients: state.clients?.length || 0,
    employees: state.employees?.length || 0,
    properties: state.properties?.length || 0,
    estimates: state.estimates?.length || 0,
    jobs: state.jobs?.length || 0,
    invoices: state.invoices?.length || 0
  };

  const totalRecords = Object.values(dataCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your application settings and data</p>
      </div>

      {/* User Info */}
      <div className="settings-section">
        <div className="section-header">
          <User size={20} />
          <h2>Account</h2>
        </div>
        <div className="settings-card">
          <div className="user-info-row">
            <span className="label">Email</span>
            <span className="value">{user?.email}</span>
          </div>
          <div className="user-info-row">
            <span className="label">User ID</span>
            <span className="value mono">{user?.uid}</span>
          </div>
        </div>
      </div>

      {/* Master Password */}
      <div className="settings-section">
        <div className="section-header">
          <Lock size={20} />
          <h2>Master Password</h2>
        </div>

        {passwordMessage && (
          <div className={`message ${passwordMessage.type}`}>
            {passwordMessage.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
            {passwordMessage.text}
          </div>
        )}

        <div className="settings-card">
          <div className="action-info" style={{ marginBottom: 16 }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
              The master password is required to delete clients. Only the owner/manager should know this password.
              {!hasExistingPassword && <strong> No master password has been set yet.</strong>}
            </p>
          </div>

          {hasExistingPassword && (
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>Current Master Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                className="form-input"
              />
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 12 }}>
            <label>{hasExistingPassword ? 'New Master Password' : 'Set Master Password'}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="form-input"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label>Confirm {hasExistingPassword ? 'New ' : ''}Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              className="form-input"
              onKeyDown={(e) => e.key === 'Enter' && handleSetMasterPassword()}
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSetMasterPassword}
            disabled={savingPassword}
          >
            {savingPassword ? (
              <><RefreshCw size={18} className="spin" /> Saving...</>
            ) : (
              <><Lock size={18} /> {hasExistingPassword ? 'Update Master Password' : 'Set Master Password'}</>
            )}
          </button>
        </div>
      </div>

      {/* Database Stats */}
      <div className="settings-section">
        <div className="section-header">
          <Database size={20} />
          <h2>Database</h2>
        </div>
        
        <div className="settings-card">
          <div className="db-stats">
            <div className="db-stat">
              <span className="stat-value">{dataCounts.clients}</span>
              <span className="stat-label">Clients</span>
            </div>
            <div className="db-stat">
              <span className="stat-value">{dataCounts.employees}</span>
              <span className="stat-label">Employees</span>
            </div>
            <div className="db-stat">
              <span className="stat-value">{dataCounts.properties}</span>
              <span className="stat-label">Properties</span>
            </div>
            <div className="db-stat">
              <span className="stat-value">{dataCounts.estimates}</span>
              <span className="stat-label">Estimates</span>
            </div>
            <div className="db-stat">
              <span className="stat-value">{dataCounts.jobs}</span>
              <span className="stat-label">Jobs</span>
            </div>
            <div className="db-stat">
              <span className="stat-value">{dataCounts.invoices}</span>
              <span className="stat-label">Invoices</span>
            </div>
          </div>
          <div className="db-total">
            <strong>{totalRecords}</strong> total records in Firestore
          </div>
        </div>
      </div>

      {/* Database Actions */}
      <div className="settings-section">
        <div className="section-header">
          <Shield size={20} />
          <h2>Data Management</h2>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
            {message.text}
          </div>
        )}

        <div className="settings-card">
          <div className="action-item">
            <div className="action-info">
              <h3><Upload size={18} /> Load Sample Data</h3>
              <p>Populate the database with sample clients, employees, jobs, and estimates for testing.</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleSeedDatabase}
              disabled={seeding}
            >
              {seeding ? (
                <><RefreshCw size={18} className="spin" /> Loading...</>
              ) : (
                <><Database size={18} /> Load Sample Data</>
              )}
            </button>
          </div>
        </div>

        {backfillMessage && (
          <div className={`message ${backfillMessage.type}`}>
            {backfillMessage.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
            {backfillMessage.text}
          </div>
        )}

        <div className="settings-card">
          <div className="action-item">
            <div className="action-info">
              <h3><Database size={18} /> Backfill Sales-Analytics Fields</h3>
              <p>One-time migration: ensure every estimate has <code>source</code>, <code>serviceType</code>, <code>assignedTo</code>, <code>serviceArea</code>, <code>quoteAmount</code>, <code>finalJobValue</code>, <code>lostReason</code>, and a seeded <code>stageHistory</code>. Safe to re-run.</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleBackfillSalesAnalytics}
              disabled={backfilling}
            >
              {backfilling ? (
                <><RefreshCw size={18} className="spin" /> Backfilling...</>
              ) : (
                <><Database size={18} /> Run Backfill</>
              )}
            </button>
          </div>
        </div>

        {metricsBackfillMessage && (
          <div className={`message ${metricsBackfillMessage.type}`}>
            {metricsBackfillMessage.type === 'success' ? <Check size={18} /> : <AlertTriangle size={18} />}
            {metricsBackfillMessage.text}
          </div>
        )}

        <div className="settings-card">
          <div className="action-item">
            <div className="action-info">
              <h3><Database size={18} /> Rebuild Sales Metrics (12 months)</h3>
              <p>Rebuilds <code>salesMetrics/{'{YYYY-MM-DD}'}</code> daily docs for the trailing 12 months from existing estimates. Run once after enabling Phase 2; ongoing writes are kept current automatically.</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleBackfillSalesMetrics}
              disabled={metricsBackfilling}
            >
              {metricsBackfilling ? (
                <><RefreshCw size={18} className="spin" /> Rebuilding...</>
              ) : (
                <><Database size={18} /> Rebuild Daily Metrics</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* App Info */}
      <div className="settings-section">
        <div className="section-header">
          <Smartphone size={20} />
          <h2>Team App</h2>
        </div>
        <div className="settings-card">
          <div className="action-item">
            <div className="action-info">
              <h3><Smartphone size={18} /> Mobile View for Field Teams</h3>
              <p>Simplified interface for cleaners to view their schedule, clock in/out, and upload photos.</p>
            </div>
            <a href="/team" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              Open Team App
            </a>
          </div>
        </div>
      </div>

      {/* App Info */}
      <div className="settings-section">
        <div className="section-header">
          <Building size={20} />
          <h2>Application</h2>
        </div>
        <div className="settings-card">
          <div className="user-info-row">
            <span className="label">App Name</span>
            <span className="value">Keel Group Operations</span>
          </div>
          <div className="user-info-row">
            <span className="label">Version</span>
            <span className="value">1.0.0 (Phase 1B)</span>
          </div>
          <div className="user-info-row">
            <span className="label">Database</span>
            <span className="value">Firebase Firestore</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
